import { useEffect, useState } from "react";
import { Box, Stack } from "@mui/material";
import { Alert, Button, Typography } from "@platform/design-system";

import { useAnalystNote } from "../../hooks/useAnalystNote";
import { useAutosaveFlush } from "../../hooks/useAutosaveFlush";
import useToastNotificationService from "../../hooks/useToastNotificationService";
import RichTextEditor from "../../pages/RFPDetails/components/Present/shared/RichTextEditor/RichTextEditor";

import { MAX_CHARACTERS } from "./constants";

interface AnalystNoteContentProps {
  ecoRfpId: string;
  // Lets the drawer save this panel when the analyst leaves the tab.
  registerFlush?: (flush: () => void) => void;
}

const AnalystNoteContent = ({
  ecoRfpId,
  registerFlush,
}: AnalystNoteContentProps): React.JSX.Element => {
  const noteHook = useAnalystNote(ecoRfpId);
  const { createToastNotification } = useToastNotificationService();
  const savedContent = noteHook.content;
  const [editorContent, setEditorContent] = useState(savedContent);
  const [characterCount, setCharacterCount] = useState(0);

  // Reset the editor to the saved content on initial load and after a save
  // invalidates the query. Editing does not change savedContent, so in-flight
  // edits are never clobbered.
  useEffect(() => {
    setEditorContent(savedContent);
  }, [savedContent]);

  const isEditorEmpty = characterCount === 0;
  const isSavedEmpty = savedContent.trim() === "";
  const hasUnsavedChanges =
    isEditorEmpty && isSavedEmpty ? false : editorContent !== savedContent;

  const handleSave = async (): Promise<void> => {
    try {
      await noteHook.upsertNoteAsync({
        content: isEditorEmpty ? "" : editorContent,
      });
      createToastNotification({
        severity: "success",
        title: "Notes saved",
        message: "Your analyst notes were saved.",
      });
    } catch {
      createToastNotification({
        severity: "error",
        title: "Saving failed",
        message: "Your analyst notes weren't saved. Please try again.",
      });
    }
  };

  const handleCancel = (): void => {
    setEditorContent(savedContent);
  };

  const notifyLimitExceeded = (): void => {
    createToastNotification({
      severity: "error",
      title: "Character limit reached",
      message: `Notes are limited to ${MAX_CHARACTERS.toLocaleString()} characters. Some pasted text wasn't added.`,
    });
  };

  useAutosaveFlush(() => {
    if (!hasUnsavedChanges) return;
    void noteHook
      .upsertNoteAsync({ content: isEditorEmpty ? "" : editorContent })
      .catch(() => {
        createToastNotification({
          severity: "error",
          title: "Saving failed",
          message: "Your analyst notes weren't saved. Please try again.",
        });
      });
  }, registerFlush);

  if (noteHook.isLoading) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body-medium" color="textSecondary">
          Loading notes...
        </Typography>
      </Box>
    );
  }

  if (noteHook.isError) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Typography variant="body-medium" color="danger">
          Failed to load notes. Please try again.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      role="tabpanel"
      id="tabpanel-analyst-notepad"
      aria-labelledby="tab-analyst-notepad"
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        flex: 1,
        minHeight: 0,
      }}
    >
      <Alert severity="info">
        <strong>Internal Notes:</strong> These notes are intended for internal
        collaboration and reference. These notes are not visible to carriers or
        brokers.
      </Alert>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          border: 1,
          borderColor: "divider",
          borderRadius: 1,
        }}
      >
        <RichTextEditor
          content={editorContent}
          placeholder="Enter notes ..."
          onUpdate={setEditorContent}
          onCharacterCount={setCharacterCount}
          maxCharacters={MAX_CHARACTERS}
          onLimitExceeded={notifyLimitExceeded}
        />
        <Typography
          variant="caption"
          color={characterCount >= MAX_CHARACTERS ? "danger" : "textSecondary"}
          sx={{ alignSelf: "flex-end", px: 2, py: 1 }}
        >
          {characterCount}/{MAX_CHARACTERS}
        </Typography>
      </Box>
      <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
        <Button
          variant="tertiary"
          onClick={handleCancel}
          disabled={!hasUnsavedChanges || noteHook.isUpserting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!hasUnsavedChanges || noteHook.isUpserting}
          onClick={handleSave}
        >
          Save
        </Button>
      </Stack>
    </Box>
  );
};

export default AnalystNoteContent;
